import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import _ from 'lodash';

import { profileSelector } from 'src/store/profile';
import { adminDataSelector, logoutAdminUser } from 'src/store/adminAuth';
import { ThemeContext } from 'src/hoc';

import { CustomButton, CustomLink, LanguageDropdown } from 'src/components';

import { ROUTE } from 'src/constants';

import { DarkLogo, LetterIcon, TelegramIcon, WhiteLogo } from 'src/assets/header';
import { ReactComponent as UserIcon } from 'src/assets/header/UserIcon.svg';
import { ReactComponent as MoonIcon } from 'src/assets/header/MoonIcon.svg';
import './header.scss';

export const HeaderLogo = () => {
  const { theme } = useContext(ThemeContext);

  const { pathname } = useLocation();

  const normalizeLogoRoute = useCallback(() => {
    if (pathname.includes('dashboard')) {
      return ROUTE.ADMIN.ORDERS.MAIN;
    }

    return ROUTE.HOME;
  }, [pathname]);

  return (
    <Link className="header--left" to={normalizeLogoRoute()}>
      {/* <img className="header--left__image" src={Logo} alt={Logo} />
      LOGO */}
      <img
        className="header--left__image"
        src={theme === 'light' ? DarkLogo : WhiteLogo}
        alt={theme === 'light' ? DarkLogo : WhiteLogo}
      />
    </Link>
  );
};

export const HeaderInfo = () => {
  const { t } = useTranslation();

  const { pathname } = useLocation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const adminData = useSelector(adminDataSelector);

  const onClickLogout = useCallback(() => {
    dispatch(logoutAdminUser({ navigate }));
  }, [dispatch, navigate]);

  const isAdmin = pathname.includes('/dashboard/') && !pathname.includes('/dashboard/auth/login');

  return (
    <div className="header--info">
      <Link className="header--info__item" to="tg://resolve?domain=exok_manager">
        <img className="header--info__item__img" src={TelegramIcon} alt={TelegramIcon} />
        Telegram
      </Link>
      <Link className="header--info__item" to="tg://resolve?domain=ExOk_Exchange_bot">
        <img className="header--info__item__img" src={TelegramIcon} alt={TelegramIcon} />
        Telegram Bot
      </Link>

      <Link className="header--info__item" to="mailto:support@exok.cash">
        <img className="header--info__item__img" src={LetterIcon} alt={LetterIcon} />
        E-mail
      </Link>

      {isAdmin ? (
        <div className="header--info__admin">
          <CustomButton title={adminData?.email} variant="filled" icon={<UserIcon />} />
          <CustomButton
            title={t('title:logOut')}
            color="red"
            variant="filled"
            onClick={onClickLogout}
          />
        </div>
      ) : null}
    </div>
  );
};

export const Header = () => {
  const { t } = useTranslation('header');

  const { theme, toggleTheme } = useContext(ThemeContext);

  const navigate = useNavigate();

  const user = useSelector(profileSelector);

  const isLoggedIn = !_.isEmpty(user);

  const menuRef = useRef();
  const mobileRef = useRef();

  const [isMenu, setIsMenu] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        mobileRef.current &&
        !mobileRef.current.contains(event.target)
      ) {
        setIsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef, mobileRef]);

  const onClickProfile = useCallback(() => {
    if (isLoggedIn) {
      navigate(ROUTE.PERSONAL_ACCOUNT);
    } else {
      navigate(ROUTE.AUTH.SIGN_IN);
    }

    setIsMenu(false);
  }, [navigate, isLoggedIn]);

  const onClickTheme = useCallback(() => {
    setIsMenu(false);
    toggleTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, toggleTheme]);

  const onSelectLanguage = useCallback(() => {
    setIsMenu(false);
  }, []);

  const onClickMenu = useCallback(() => {
    setIsMenu((prev) => !prev);
  }, []);

  const onCloseMenu = useCallback(() => {
    setIsMenu(false);
  }, []);

  const headerStyles = classNames('header', {
    'header--active': isMenu,
  });

  const headerMenuStyles = classNames('header__menu', {
    'header__menu--active': isMenu,
    'header__menu--closing': !isMenu,
  });

  const headerButtonStyles = classNames('header__mobile', {
    'header__mobile--active': isMenu,
  });

  const onClickLink = useCallback(() => {
    document.querySelector('body').scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="header--wrapper">
      <HeaderInfo />
      <div className={headerStyles}>
        <HeaderLogo />
        <div className="header--navigation">
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.HOME}
            title={t('exchange')}
            onClick={onClickLink}
          />
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.EXCHANGE.DIRECTIONS}
            title={t('directions')}
            onClick={onClickLink}
          />
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.REVIEWS}
            title={t('reviews')}
            onClick={onClickLink}
          />
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.ABOUT_US}
            title={t('aboutUs')}
            onClick={onClickLink}
          />
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.AGREEMENT}
            title={t('accept')}
            onClick={onClickLink}
          />
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.CONTACTS}
            title={t('contacts')}
            onClick={onClickLink}
          />
        </div>
        <div className="header--right">
          <CustomButton
            icon={<MoonIcon />}
            variant="filled"
            color="default"
            onClick={onClickTheme}
          />

          <LanguageDropdown />

          <CustomButton
            title={t('ownCabinet')}
            icon={<UserIcon />}
            variant="filled"
            onClick={onClickProfile}
          />
        </div>

        <div className={headerButtonStyles} onClick={onClickMenu} ref={mobileRef}>
          <span />
          <span />
          <span />
        </div>
        <div className={headerMenuStyles} ref={menuRef}>
          <div className="header__menu__row">
            <div className="header__menu__row__items" style={{ display: 'flex' }}>
              <CustomButton
                icon={<MoonIcon />}
                variant="filled"
                color="default"
                onClick={onClickTheme}
              />

              <LanguageDropdown onSelect={onSelectLanguage} />
            </div>

            <CustomButton
              title={t('ownCabinet')}
              icon={<UserIcon />}
              variant="filled"
              onClick={onClickProfile}
            />
          </div>
          <CustomLink
            className="header--navigation__item"
            to="/"
            title={t('exchange')}
            onClick={onCloseMenu}
          />
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.EXCHANGE.DIRECTIONS}
            title={t('directions')}
            onClick={onCloseMenu}
          />
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.REVIEWS}
            title={t('reviews')}
            onClick={onCloseMenu}
          />
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.ABOUT_US}
            title={t('aboutUs')}
            onClick={onCloseMenu}
          />
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.AGREEMENT}
            title={t('accept')}
            onClick={onCloseMenu}
          />
          <CustomLink
            className="header--navigation__item"
            to={ROUTE.CONTACTS}
            title={t('contacts')}
            onClick={onCloseMenu}
          />
        </div>
      </div>
    </div>
  );
};
